/**
 * PageStatusGate — wraps every public route. Inspects the current
 * pathname against the page-status table and either:
 *   • Renders the children (status === 'live')
 *   • Renders a 404-style screen (status === 'hidden')
 *   • Renders the maintenance / coming-soon screen otherwise
 *
 * Admins see exactly what visitors see — so a "coming soon" toggle
 * actually shows the coming-soon screen. A small floating toolbar
 * lets them flip back to the real page (`?preview=admin`) for
 * authoring without changing the public status.
 *
 * The store data refreshes every 60s, so a status change reflects
 * automatically for visitors without a hard reload.
 */

import { useLocation } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import NotFoundPage from '@/app/routes/not-found'
import { usePageStatusStore, resolvePageStatus } from './store'
import { StatusScreen } from './status-screen'
import { useCurrentUser } from '@/features/auth/store'

interface PageStatusGateProps {
  children: React.ReactNode
}

export function PageStatusGate({ children }: PageStatusGateProps) {
  const location = useLocation()
  const list = usePageStatusStore((s) => s.list)
  const loaded = usePageStatusStore((s) => s.loaded)
  const user = useCurrentUser()

  const params = new URLSearchParams(location.search)
  const adminPreview = params.get('preview') === 'admin'
  const isAdmin = user?.role === 'admin'

  // Not loaded yet → render the page (don't block on the network).
  if (!loaded) return <>{children}</>

  const match = resolvePageStatus(location.pathname, list)

  // Admin override: `?preview=admin` skips the gate so the admin can
  // view / edit the real page even when it's gated for visitors.
  if (isAdmin && adminPreview && match && match.status !== 'live') {
    return (
      <>
        <AdminPreviewBanner status={match.status} pathname={location.pathname} />
        {children}
      </>
    )
  }

  if (!match) return <>{children}</>

  if (match.status === 'live') return <>{children}</>
  if (match.status === 'hidden') {
    return isAdmin ? (
      <>
        <AdminGatedHint status={match.status} pathname={location.pathname} />
        <NotFoundPage />
      </>
    ) : (
      <NotFoundPage />
    )
  }
  if (match.status === 'maintenance')
    return (
      <>
        {isAdmin && (
          <AdminGatedHint status={match.status} pathname={location.pathname} />
        )}
        <StatusScreen kind="maintenance" page={match} />
      </>
    )
  if (match.status === 'coming_soon')
    return (
      <>
        {isAdmin && (
          <AdminGatedHint status={match.status} pathname={location.pathname} />
        )}
        <StatusScreen kind="coming_soon" page={match} />
      </>
    )

  return <>{children}</>
}

const STATUS_LABEL_AR: Record<'hidden' | 'maintenance' | 'coming_soon', string> = {
  hidden: 'مخفية',
  maintenance: 'تحت الصيانة',
  coming_soon: 'قريباً',
}

/**
 * Floating chip that appears for admins on a gated page. Clicking
 * "افتح الصفحة" appends `?preview=admin` so the admin can author the
 * real content without flipping the public status.
 */
function AdminGatedHint({
  status,
  pathname,
}: {
  status: 'hidden' | 'maintenance' | 'coming_soon'
  pathname: string
}) {
  const previewHref = `${pathname}?preview=admin`
  return (
    <div className="fixed bottom-4 start-4 z-[60] flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-xs font-medium text-amber-100 shadow-lg backdrop-blur-md">
      <Eye className="h-3.5 w-3.5" aria-hidden />
      <span>
        أدمن — الصفحة دلوقت <strong>{STATUS_LABEL_AR[status]}</strong> للزوار
      </span>
      <a
        href={previewHref}
        className="rounded-full bg-amber-500/30 px-2.5 py-1 font-semibold text-amber-50 transition-colors hover:bg-amber-500/40"
      >
        افتح الصفحة
      </a>
    </div>
  )
}

/**
 * Banner shown once an admin clicks `افتح الصفحة` — confirms they're
 * viewing the real (gated) page and offers a one-click way back to
 * the visitor experience.
 */
function AdminPreviewBanner({
  status,
  pathname,
}: {
  status: 'hidden' | 'maintenance' | 'coming_soon'
  pathname: string
}) {
  return (
    <div
      role="alert"
      className="sticky top-0 z-[55] flex flex-wrap items-center justify-center gap-2 border-b border-amber-500/40 bg-amber-500/15 px-4 py-2 text-xs font-medium text-amber-100 backdrop-blur-md"
    >
      <span className="inline-flex items-center gap-1.5">
        <EyeOff className="h-3.5 w-3.5" aria-hidden />
        أنت بتحرر الصفحة كأدمن — الزوار حالياً يرون: <strong className="font-bold">{STATUS_LABEL_AR[status]}</strong>
      </span>
      <a
        href={pathname}
        className="rounded-full bg-amber-500/30 px-3 py-1 font-semibold text-amber-50 transition-colors hover:bg-amber-500/40"
      >
        ارجع لـ معاينة الزائر
      </a>
    </div>
  )
}
